// src/pages/RecruitingPipeline.jsx
// Recruiting Pipeline — Kanban board, Agent Roster, and Analytics views
// Full spec: 7 stages, AI Fit Score, drag-to-advance, carrier contracting, detail slide-out

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, UserPlus, BarChart3, LayoutGrid, Table2,
  Search, Filter, X, ChevronDown, ChevronRight,
  ExternalLink, CheckCircle2, Circle, Clock, ArrowRight,
  RefreshCw, AlertTriangle, Star, Award, Briefcase,
  Phone, Mail, Shield, TrendingUp, Plus, Check,
  GripVertical, FileText, MessageSquare, Activity,
  ChevronUp, Minus
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STAGES = [
  { key: 'prospect',              label: 'Prospect',                    color: 'text-slate-400',   border: 'border-slate-500',   bg: 'bg-slate-500' },
  { key: 'contacted',             label: 'Contacted',                   color: 'text-blue-400',    border: 'border-blue-400',    bg: 'bg-blue-400' },
  { key: 'interview_scheduled',   label: 'Interview Scheduled',         color: 'text-purple-400',  border: 'border-purple-400',  bg: 'bg-purple-400' },
  { key: 'contracting',           label: 'Contracting',                 color: 'text-amber-500',   border: 'border-amber-500',   bg: 'bg-amber-500' },
  { key: 'awaiting_appointments', label: 'Awaiting Appointments',       color: 'text-orange-400',  border: 'border-orange-400',  bg: 'bg-orange-400' },
  { key: 'ready_to_sell',         label: 'Ready to Sell',               color: 'text-emerald-400', border: 'border-emerald-400', bg: 'bg-emerald-400' },
  { key: 'active',                label: 'Active',                      color: 'text-green-500',   border: 'border-green-500',   bg: 'bg-green-500' },
];

const STAGE_SLA = {
  prospect: 7, contacted: 14, interview_scheduled: 10,
  contracting: 14, awaiting_appointments: 30, ready_to_sell: 14, active: 9999,
};

const SOURCE_CONFIG = {
  referral:      { label: 'Referral',       bg: 'bg-green-500/20',  text: 'text-green-400',  border: 'border-green-500/30' },
  linkedin:      { label: 'LinkedIn',       bg: 'bg-blue-500/20',   text: 'text-blue-400',   border: 'border-blue-500/30' },
  indeed:        { label: 'Indeed',         bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500/30' },
  carrier_event: { label: 'Carrier Event',  bg: 'bg-amber-500/20',  text: 'text-amber-400',  border: 'border-amber-500/30' },
  cold_outreach: { label: 'Cold Outreach',  bg: 'bg-slate-500/20',  text: 'text-slate-400',  border: 'border-slate-500/30' },
  other:         { label: 'Other',          bg: 'bg-gray-500/20',   text: 'text-gray-400',   border: 'border-gray-500/30' },
};

const CARRIER_PORTALS = {
  Humana: 'https://agentlink.humana.com',
  Aetna:  'https://www.aetnaproducerworld.com',
  UHC:    'https://www.uhcproducers.com',
  Anthem: 'https://www.anthem.com/broker',
  Cigna:  'https://www.cignaforbrokers.com',
};

const DEFAULT_CARRIERS = Object.keys(CARRIER_PORTALS);

const CONTRACT_STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'text-gray-400', bg: 'bg-gray-800' },
  invited:     { label: 'Invited',     color: 'text-blue-400', bg: 'bg-blue-900/40' },
  submitted:   { label: 'Submitted',   color: 'text-amber-400',bg: 'bg-amber-900/40' },
  appointed:   { label: 'Appointed',   color: 'text-green-400',bg: 'bg-green-900/40' },
  terminated:  { label: 'Terminated',  color: 'text-red-400',  bg: 'bg-red-900/40' },
};

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
function daysLabel(n) {
  if (n === 0) return 'Today';
  if (n === 1) return '1 day';
  return `${n} days`;
}

function daysColor(days, sla) {
  if (days <= 7)   return 'text-green-400';
  if (days <= 14)  return 'text-amber-400';
  return 'text-red-400';
}

function fitScoreColor(score) {
  if (score === null || score === undefined) return 'text-gray-500 bg-gray-800';
  if (score >= 80) return 'text-green-400 bg-green-900/40';
  if (score >= 60) return 'text-amber-400 bg-amber-900/40';
  return 'text-red-400 bg-red-900/40';
}

function timeAgo(ts) {
  if (!ts) return null;
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30)  return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SourceBadge({ source }) {
  const cfg = SOURCE_CONFIG[source] || SOURCE_CONFIG.other;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

function NpnBadge({ verified, issues, npn }) {
  if (!npn) return <span className="w-2 h-2 rounded-full bg-gray-600 inline-block" title="No NPN" />;
  if (issues) return <span className="w-2 h-2 rounded-full bg-red-500 inline-block" title="NPN issues" />;
  if (verified) return <span className="w-2 h-2 rounded-full bg-green-500 inline-block" title="NPN verified" />;
  return <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" title="NPN unverified" />;
}

function FitScoreBadge({ score }) {
  if (score === null || score === undefined) return (
    <span className="text-xs text-gray-600">—</span>
  );
  const cls = fitScoreColor(score);
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold tabular-nums ${cls}`}>
      <Star size={9} className="opacity-70" />
      {score}
    </span>
  );
}

function StagePill({ stage }) {
  const s = STAGES.find(s => s.key === stage);
  if (!s) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.color} bg-gray-800`}>
      {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Recruit Card (Kanban)
// ---------------------------------------------------------------------------
function RecruitCard({ recruit, onClick, onDragStart }) {
  const sla    = STAGE_SLA[recruit.stage] || 14;
  const days   = recruit.daysInStage || 0;
  const over   = recruit.isOverdue;
  const dColor = daysColor(days, sla);
  const progress = recruit.carriersTotal > 0
    ? Math.round((recruit.carriersAppointed / recruit.carriersTotal) * 100)
    : 0;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className={`
        relative bg-gray-900 border border-gray-800 rounded-lg p-3 cursor-pointer
        hover:border-gray-700 hover:bg-gray-850 transition-all select-none
        ${over ? 'border-r-2 border-r-red-500' : ''}
      `}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <NpnBadge verified={recruit.npnVerified} issues={recruit.npnIssues} npn={recruit.npn} />
          <span className="text-sm font-semibold text-white truncate">{recruit.fullName}</span>
        </div>
        <FitScoreBadge score={recruit.aiFitScore} />
      </div>

      {/* Source */}
      <div className="mb-2">
        <SourceBadge source={recruit.source} />
      </div>

      {/* Carrier progress */}
      {recruit.carriersTotal > 0 && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Carriers</span>
            <span className="tabular-nums">{recruit.carriersAppointed}/{recruit.carriersTotal}</span>
          </div>
          <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Days in stage */}
      <div className="flex items-center justify-between text-xs mt-1">
        <span className={`flex items-center gap-1 ${dColor} font-medium tabular-nums`}>
          <Clock size={10} />
          {daysLabel(days)} in stage
        </span>
        {recruit.assignedManagerName && (
          <span className="flex items-center gap-1 text-gray-500">
            <div className="w-4 h-4 rounded-full bg-gray-700 flex items-center justify-center text-[9px] font-bold text-gray-300">
              {initials(recruit.assignedManagerName)}
            </div>
          </span>
        )}
      </div>

      {/* Updated at */}
      {recruit.updatedAt && (
        <div className="text-xs text-gray-600 mt-1">{timeAgo(recruit.updatedAt)}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kanban Column
// ---------------------------------------------------------------------------
function KanbanColumn({ stage, recruits, onCardClick, onDragStart, onDrop, onDragOver }) {
  const s = STAGES.find(s => s.key === stage.key);
  const avgDays = recruits.length > 0
    ? Math.round(recruits.reduce((sum, r) => sum + (r.daysInStage || 0), 0) / recruits.length)
    : 0;

  return (
    <div
      className="flex flex-col min-w-[280px] w-[280px]"
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, stage.key)}
    >
      {/* Column header */}
      <div className={`border-t-2 ${s.border} bg-gray-900 rounded-t-lg px-3 pt-3 pb-2`}>
        <div className="flex items-center justify-between">
          <span className={`text-sm font-semibold ${s.color}`}>{stage.label}</span>
          <span className="bg-gray-800 text-gray-300 text-xs font-bold px-2 py-0.5 rounded-full tabular-nums">
            {recruits.length}
          </span>
        </div>
        {recruits.length > 0 && (
          <div className="text-xs text-gray-600 mt-0.5 tabular-nums">
            avg {avgDays}d in stage
          </div>
        )}
      </div>

      {/* Cards */}
      <div className="flex-1 bg-gray-950 rounded-b-lg px-2 pb-2 pt-1 min-h-[200px] space-y-2">
        {recruits.length === 0 ? (
          <div className="mt-4 border-2 border-dashed border-gray-800 rounded-lg p-4 text-center">
            <span className="text-xs text-gray-700">No recruits — drag a card here</span>
          </div>
        ) : (
          recruits.map(r => (
            <RecruitCard
              key={r.id}
              recruit={r}
              onClick={() => onCardClick(r)}
              onDragStart={(e) => onDragStart(e, r)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Recruit Modal
// ---------------------------------------------------------------------------
function AddRecruitModal({ onClose, onSave, managers }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    npn: '', residentState: '', source: 'other', sourceDetail: '',
    assignedManagerId: '', priorExperience: false,
    initialCarriers: [...DEFAULT_CARRIERS],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First name and last name are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${BASE}/api/recruits`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Failed to create recruit');
      onSave(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleCarrier = (c) => {
    setForm(f => ({
      ...f,
      initialCarriers: f.initialCarriers.includes(c)
        ? f.initialCarriers.filter(x => x !== c)
        : [...f.initialCarriers, c],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">Add New Recruit</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 mb-4 text-sm text-red-300">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">First Name *</label>
              <input
                type="text"
                value={form.firstName}
                onChange={e => set('firstName', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Last Name *</label>
              <input
                type="text"
                value={form.lastName}
                onChange={e => set('lastName', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => set('email', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => set('phone', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">NPN</label>
              <input
                type="text"
                value={form.npn}
                onChange={e => set('npn', e.target.value)}
                placeholder="National Producer Number"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Resident State</label>
              <input
                type="text"
                value={form.residentState}
                onChange={e => set('residentState', e.target.value.toUpperCase().slice(0, 2))}
                placeholder="e.g. MN"
                maxLength={2}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Source</label>
              <select
                value={form.source}
                onChange={e => set('source', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
              >
                {Object.entries(SOURCE_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Assigned Manager</label>
              <select
                value={form.assignedManagerId}
                onChange={e => set('assignedManagerId', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
              >
                <option value="">Self</option>
                {(managers || []).map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Source Detail (optional)</label>
            <input
              type="text"
              value={form.sourceDetail}
              onChange={e => set('sourceDetail', e.target.value)}
              placeholder="e.g. Referred by John Smith"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="priorExp"
              type="checkbox"
              checked={form.priorExperience}
              onChange={e => set('priorExperience', e.target.checked)}
              className="w-4 h-4 accent-amber-400"
            />
            <label htmlFor="priorExp" className="text-sm text-gray-300">Prior insurance experience</label>
          </div>

          {/* Carrier checkboxes */}
          <div>
            <label className="block text-xs text-gray-400 mb-2">Initial Carriers to Track</label>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_CARRIERS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCarrier(c)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                    form.initialCarriers.includes(c)
                      ? 'bg-amber-500/20 border-amber-500/40 text-amber-400'
                      : 'bg-gray-800 border-gray-700 text-gray-500'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg text-sm hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-amber-400 text-gray-950 rounded-lg text-sm font-semibold hover:bg-amber-300 transition-colors disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Recruit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail Slide-out
// ---------------------------------------------------------------------------
function DetailSlideout({ recruit: initialRecruit, onClose, onUpdate }) {
  const [recruit, setRecruit] = useState(initialRecruit);
  const [activeTab, setActiveTab] = useState('overview');
  const [activity, setActivity] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [stageSaving, setStageSaving] = useState(false);

  const id = recruit.id;

  // Load activity on tab switch
  useEffect(() => {
    if (activeTab === 'activity' && activity.length === 0) {
      setLoadingActivity(true);
      fetch(`${BASE}/api/recruits/${id}/activity`, { credentials: 'include' })
        .then(r => r.json())
        .then(j => { if (j.ok) setActivity(j.data); })
        .catch(console.error)
        .finally(() => setLoadingActivity(false));
    }
  }, [activeTab, id, activity.length]);

  // Load contracts on tab switch
  useEffect(() => {
    if (activeTab === 'contracting') {
      setLoadingContracts(true);
      fetch(`${BASE}/api/recruits/${id}/contracts`, { credentials: 'include' })
        .then(r => r.json())
        .then(j => { if (j.ok) setContracts(j.data); })
        .catch(console.error)
        .finally(() => setLoadingContracts(false));
    }
  }, [activeTab, id]);

  const handleStageChange = async (newStage) => {
    if (newStage === recruit.stage) return;
    setStageSaving(true);
    try {
      const res = await fetch(`${BASE}/api/recruits/${id}/stage`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: newStage }),
      });
      const json = await res.json();
      if (json.ok) {
        setRecruit(json.data);
        onUpdate(json.data);
        // Reset activity so it reloads
        setActivity([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setStageSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${BASE}/api/recruits/${id}/notes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: noteText }),
      });
      const json = await res.json();
      if (json.ok) {
        setNoteText('');
        setActivity(prev => [json.data, ...prev]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const res = await fetch(`${BASE}/api/recruits/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      const json = await res.json();
      if (json.ok) {
        setRecruit(json.data);
        onUpdate(json.data);
        setEditing(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleBulkInvite = async () => {
    try {
      const res = await fetch(`${BASE}/api/recruits/${id}/contracts/bulk-invite`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (json.ok) setContracts(json.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleContractUpdate = async (carrier, fields) => {
    try {
      const res = await fetch(`${BASE}/api/recruits/${id}/contracts/${encodeURIComponent(carrier)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      const json = await res.json();
      if (json.ok) {
        setContracts(prev => prev.map(c => c.carrier_name === carrier ? json.data : c));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const curStageIdx = STAGES.findIndex(s => s.key === recruit.stage);
  const nextStage   = STAGES[curStageIdx + 1] || null;
  const prevStage   = STAGES[curStageIdx - 1] || null;
  const curStageCfg = STAGES[curStageIdx];

  const TABS = [
    { key: 'overview',    label: 'Overview',    icon: FileText },
    { key: 'contracting', label: 'Contracting', icon: Briefcase },
    { key: 'activity',    label: 'Activity',    icon: Activity },
  ];

  return (
    <div className="fixed inset-0 z-40 flex" onClick={onClose}>
      {/* Backdrop */}
      <div className="flex-1 bg-black/50" />

      {/* Panel */}
      <div
        className="w-[520px] bg-gray-950 border-l border-gray-800 flex flex-col h-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-bold text-white">{recruit.fullName}</h2>
            <div className="flex items-center gap-2 mt-1">
              <StagePill stage={recruit.stage} />
              <SourceBadge source={recruit.source} />
              <FitScoreBadge score={recruit.aiFitScore} />
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 mt-1">
            <X size={20} />
          </button>
        </div>

        {/* Stage advance/revert bar */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800 bg-gray-900">
          {prevStage && (
            <button
              onClick={() => handleStageChange(prevStage.key)}
              disabled={stageSaving}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-400 text-xs hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <ChevronDown size={12} />
              {prevStage.label}
            </button>
          )}
          <div className="flex-1 text-center">
            <span className={`text-xs font-medium ${curStageCfg?.color}`}>
              {curStageCfg?.label} · {daysLabel(recruit.daysInStage || 0)}
            </span>
          </div>
          {nextStage && (
            <button
              onClick={() => handleStageChange(nextStage.key)}
              disabled={stageSaving}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs hover:bg-amber-500/30 transition-colors disabled:opacity-50"
            >
              {nextStage.label}
              <ChevronRight size={12} />
            </button>
          )}
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-gray-800">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.key
                  ? 'border-amber-400 text-amber-400'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">

          {/* ── Overview tab ── */}
          {activeTab === 'overview' && (
            <div className="p-5 space-y-5">
              {/* Profile fields */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-300">Profile</h3>
                  {!editing ? (
                    <button
                      onClick={() => { setEditing(true); setEditForm({
                        firstName: recruit.firstName,
                        lastName:  recruit.lastName,
                        email:     recruit.email || '',
                        phone:     recruit.phone || '',
                        npn:       recruit.npn || '',
                        residentState: recruit.residentState || '',
                        source:    recruit.source,
                        sourceDetail: recruit.sourceDetail || '',
                        priorExperience: recruit.priorExperience,
                      }); }}
                      className="text-xs text-amber-400 hover:text-amber-300"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditing(false)}
                        className="text-xs text-gray-500 hover:text-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={savingEdit}
                        className="text-xs text-amber-400 hover:text-amber-300 disabled:opacity-50"
                      >
                        {savingEdit ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                {!editing ? (
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Email',       value: recruit.email },
                      { label: 'Phone',       value: recruit.phone },
                      { label: 'NPN',         value: recruit.npn },
                      { label: 'State',       value: recruit.residentState },
                      { label: 'Source',      value: SOURCE_CONFIG[recruit.source]?.label },
                      { label: 'Experience',  value: recruit.priorExperience ? 'Yes' : 'No' },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-gray-900 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-0.5">{label}</div>
                        <div className="text-sm text-white">{value || '—'}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'First Name', field: 'firstName' },
                      { label: 'Last Name',  field: 'lastName' },
                      { label: 'Email',      field: 'email',    type: 'email' },
                      { label: 'Phone',      field: 'phone',    type: 'tel' },
                      { label: 'NPN',        field: 'npn' },
                      { label: 'State',      field: 'residentState', maxLength: 2 },
                    ].map(({ label, field, type, maxLength }) => (
                      <div key={field}>
                        <label className="block text-xs text-gray-500 mb-1">{label}</label>
                        <input
                          type={type || 'text'}
                          value={editForm[field] || ''}
                          maxLength={maxLength}
                          onChange={e => setEditForm(f => ({ ...f, [field]: maxLength ? e.target.value.toUpperCase().slice(0, maxLength) : e.target.value }))}
                          className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    ))}
                    <div className="col-span-2 flex items-center gap-2">
                      <input
                        id="editExp"
                        type="checkbox"
                        checked={editForm.priorExperience || false}
                        onChange={e => setEditForm(f => ({ ...f, priorExperience: e.target.checked }))}
                        className="accent-amber-400"
                      />
                      <label htmlFor="editExp" className="text-sm text-gray-300">Prior insurance experience</label>
                    </div>
                  </div>
                )}
              </div>

              {/* NPN Status */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-1.5">
                    <Shield size={14} className="text-gray-500" />
                    NPN Verification
                  </h3>
                  {recruit.npn && (
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                      recruit.npnIssues ? 'text-red-400 bg-red-900/30' :
                      recruit.npnVerified ? 'text-green-400 bg-green-900/30' :
                      'text-gray-400 bg-gray-800'
                    }`}>
                      {recruit.npnIssues ? 'Issues Found' : recruit.npnVerified ? 'Verified' : 'Unverified'}
                    </span>
                  )}
                </div>
                {recruit.npn ? (
                  <div className="text-sm text-gray-400">
                    NPN: <span className="text-white font-mono">{recruit.npn}</span>
                    {recruit.npnVerifiedAt && (
                      <span className="ml-2 text-xs text-gray-600">· verified {timeAgo(recruit.npnVerifiedAt)}</span>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">No NPN on file</div>
                )}
              </div>

              {/* AI Fit Score */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-1.5">
                    <Star size={14} className="text-amber-400" />
                    AI Fit Score
                  </h3>
                  <FitScoreBadge score={recruit.aiFitScore} />
                </div>
                <div className="space-y-2">
                  {[
                    { label: 'Source quality',      pts: { referral: 30, carrier_event: 25, linkedin: 20, cold_outreach: 15, indeed: 10, other: 10 }[recruit.source] || 10, max: 30 },
                    { label: 'NPN verified',         pts: recruit.npnVerified && !recruit.npnIssues ? 20 : 0, max: 20 },
                    { label: 'Prior experience',     pts: recruit.priorExperience ? 15 : 0, max: 15 },
                    { label: 'Resident state set',   pts: recruit.residentState ? 15 : 0, max: 15 },
                    { label: 'Fast first reply',     pts: recruit.firstReplyAt && recruit.firstOutreachAt && (new Date(recruit.firstReplyAt) - new Date(recruit.firstOutreachAt)) / 3600000 <= 48 ? 10 : 0, max: 10 },
                    { label: 'Fast contracting',     pts: recruit.contractingStartedAt && recruit.firstOutreachAt && (new Date(recruit.contractingStartedAt) - new Date(recruit.firstOutreachAt)) / 86400000 <= 7 ? 10 : 0, max: 10 },
                  ].map(({ label, pts, max }) => (
                    <div key={label} className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500 flex-1">{label}</span>
                      <div className="w-24 h-1 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full"
                          style={{ width: `${(pts / max) * 100}%` }}
                        />
                      </div>
                      <span className={`tabular-nums w-8 text-right font-medium ${pts > 0 ? 'text-amber-400' : 'text-gray-700'}`}>
                        +{pts}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-sm font-semibold text-gray-300 mb-2">Add Note</h3>
                <div className="flex gap-2">
                  <textarea
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    rows={2}
                    placeholder="Write a note..."
                    className="flex-1 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-amber-500"
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={savingNote || !noteText.trim()}
                    className="px-3 py-2 bg-amber-400 text-gray-950 rounded-lg text-sm font-semibold hover:bg-amber-300 transition-colors disabled:opacity-40 self-end"
                  >
                    {savingNote ? '...' : 'Add'}
                  </button>
                </div>
              </div>

              {/* Recent activity preview */}
              {recruit.activity && recruit.activity.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-300 mb-2">Recent Activity</h3>
                  <div className="space-y-2">
                    {recruit.activity.slice(0, 4).map(a => (
                      <div key={a.id} className="flex items-start gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${a.is_human_activity ? 'bg-amber-400' : 'bg-gray-700'}`} />
                        <div>
                          <div className="text-xs text-gray-300">{a.description}</div>
                          <div className="text-xs text-gray-600">{a.actor_name || 'System'} · {timeAgo(a.created_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Contracting tab ── */}
          {activeTab === 'contracting' && (
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-300">Carrier Contracting</h3>
                <button
                  onClick={handleBulkInvite}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 text-amber-400 rounded-lg text-xs font-medium hover:bg-amber-500/30 transition-colors"
                >
                  <Mail size={12} />
                  Send All Invites
                </button>
              </div>

              {loadingContracts ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => <div key={i} className="h-16 bg-gray-900 rounded-lg animate-pulse" />)}
                </div>
              ) : contracts.length === 0 ? (
                <div className="text-center py-8 text-gray-600 text-sm">
                  No carriers added yet
                </div>
              ) : (
                <div className="space-y-2">
                  {contracts.map(c => {
                    const cfg = CONTRACT_STATUS_CONFIG[c.appointment_status] || CONTRACT_STATUS_CONFIG.not_started;
                    return (
                      <div key={c.carrier_name} className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-white">{c.carrier_name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${cfg.color} ${cfg.bg}`}>
                            {cfg.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {c.portalUrl && (
                            <a
                              href={c.portalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                            >
                              <ExternalLink size={10} />
                              Portal
                            </a>
                          )}
                          {c.invitation_sent_at && (
                            <span className="text-xs text-gray-600">
                              Invited {timeAgo(c.invitation_sent_at)}
                            </span>
                          )}
                          {c.appointment_status === 'not_started' && (
                            <button
                              onClick={() => handleContractUpdate(c.carrier_name, {
                                invitationSentAt: new Date().toISOString(),
                                appointmentStatus: 'invited',
                              })}
                              className="text-xs text-amber-400 hover:text-amber-300"
                            >
                              Mark Invited
                            </button>
                          )}
                          {c.appointment_status === 'invited' && (
                            <button
                              onClick={() => handleContractUpdate(c.carrier_name, {
                                appointmentStatus: 'submitted',
                              })}
                              className="text-xs text-amber-400 hover:text-amber-300"
                            >
                              Mark Submitted
                            </button>
                          )}
                          {c.appointment_status === 'submitted' && (
                            <button
                              onClick={() => handleContractUpdate(c.carrier_name, {
                                appointmentStatus: 'appointed',
                                appointmentDate: new Date().toISOString().split('T')[0],
                              })}
                              className="text-xs text-green-400 hover:text-green-300"
                            >
                              Mark Appointed
                            </button>
                          )}
                          {c.appointment_status === 'appointed' && (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                              <CheckCircle2 size={10} />
                              Appointed {c.appointment_date ? `· ${c.appointment_date}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Carrier portals reference */}
              <div className="mt-5">
                <h4 className="text-xs text-gray-600 uppercase font-semibold mb-2">Carrier Portals</h4>
                <div className="space-y-1">
                  {Object.entries(CARRIER_PORTALS).map(([name, url]) => (
                    <div key={name} className="flex items-center justify-between text-xs">
                      <span className="text-gray-400">{name}</span>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                      >
                        <ExternalLink size={10} />
                        Open
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Activity tab ── */}
          {activeTab === 'activity' && (
            <div className="p-5">
              {/* Note input */}
              <div className="mb-4 flex gap-2">
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  rows={2}
                  placeholder="Add a note..."
                  className="flex-1 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-amber-500"
                />
                <button
                  onClick={handleAddNote}
                  disabled={savingNote || !noteText.trim()}
                  className="px-3 py-2 bg-amber-400 text-gray-950 rounded-lg text-sm font-semibold hover:bg-amber-300 disabled:opacity-40 self-end"
                >
                  {savingNote ? '...' : 'Add'}
                </button>
              </div>

              {loadingActivity ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => <div key={i} className="h-12 bg-gray-900 rounded animate-pulse" />)}
                </div>
              ) : activity.length === 0 ? (
                <div className="text-center py-8 text-gray-600 text-sm">No activity yet</div>
              ) : (
                <div className="relative">
                  <div className="absolute left-2 top-0 bottom-0 w-px bg-gray-800" />
                  <div className="space-y-4 pl-7">
                    {activity.map(a => (
                      <div key={a.id} className="relative">
                        <div className={`absolute -left-5 w-2.5 h-2.5 rounded-full border-2 border-gray-950 ${
                          a.is_human_activity ? 'bg-amber-400' : 'bg-gray-700'
                        }`} />
                        <div className={`rounded-lg p-3 ${a.is_human_activity ? 'bg-gray-900' : 'bg-gray-950 border border-gray-900'}`}>
                          <div className="text-sm text-white">{a.description}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {a.actor_name || 'System'} · {timeAgo(a.created_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent Roster Table
// ---------------------------------------------------------------------------
function AgentRoster({ recruits, onRowClick, onStageChange, onArchive }) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('fullName');
  const [sortDir, setSortDir]     = useState('asc');
  const [selected, setSelected]   = useState(new Set());

  const filtered = recruits
    .filter(r => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        r.fullName?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.npn?.includes(q)
      );
    })
    .sort((a, b) => {
      const va = a[sortField] ?? '';
      const vb = b[sortField] ?? '';
      const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const sort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <Minus size={10} className="text-gray-700" />;
    return sortDir === 'asc' ? <ChevronUp size={10} className="text-amber-400" /> : <ChevronDown size={10} className="text-amber-400" />;
  };

  const toggleSelect = (id) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(r => r.id)));
  };

  const TH = ({ label, field, className = '' }) => (
    <th
      className={`px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-300 select-none ${className}`}
      onClick={() => field && sort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        {field && <SortIcon field={field} />}
      </span>
    </th>
  );

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search by name, email, or NPN..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-8 pr-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
          />
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>{selected.size} selected</span>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-gray-950 border-b border-gray-800">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={selected.size === filtered.length && filtered.length > 0}
                    onChange={toggleAll}
                    className="accent-amber-400"
                  />
                </th>
                <TH label="Name"         field="fullName" />
                <TH label="Email"        field="email" />
                <TH label="NPN" />
                <TH label="Stage"        field="stage" />
                <TH label="Days"         field="daysInStage" />
                <TH label="Source"       field="source" />
                <TH label="Carriers" />
                <TH label="Manager"      field="assignedManagerName" />
                <TH label="Updated"      field="updatedAt" />
                <th className="px-3 py-2 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-10 text-gray-600 text-sm">
                    No recruits found
                  </td>
                </tr>
              ) : (
                filtered.map(r => (
                  <tr
                    key={r.id}
                    className="hover:bg-gray-800/50 transition-colors cursor-pointer"
                    onClick={() => onRowClick(r)}
                  >
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        className="accent-amber-400"
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <NpnBadge verified={r.npnVerified} issues={r.npnIssues} npn={r.npn} />
                        <span className="text-sm font-medium text-white">{r.fullName}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-400">{r.email || '—'}</td>
                    <td className="px-3 py-2.5 text-sm text-gray-400 font-mono">{r.npn || '—'}</td>
                    <td className="px-3 py-2.5"><StagePill stage={r.stage} /></td>
                    <td className="px-3 py-2.5">
                      <span className={`text-sm tabular-nums ${daysColor(r.daysInStage || 0, STAGE_SLA[r.stage] || 14)}`}>
                        {r.daysInStage || 0}d
                      </span>
                    </td>
                    <td className="px-3 py-2.5"><SourceBadge source={r.source} /></td>
                    <td className="px-3 py-2.5 text-sm text-gray-400 tabular-nums">
                      {r.carriersTotal > 0 ? `${r.carriersAppointed}/${r.carriersTotal}` : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-500">{r.assignedManagerName || '—'}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-600">{timeAgo(r.updatedAt)}</td>
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => onArchive(r)}
                        className="text-xs text-gray-600 hover:text-red-400 transition-colors"
                      >
                        Archive
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-2 text-xs text-gray-600">
        {filtered.length} recruit{filtered.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Analytics View
// ---------------------------------------------------------------------------
function AnalyticsView({ analytics, loading }) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1,2,3].map(i => <div key={i} className="h-32 bg-gray-900 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  if (!analytics) {
    return <div className="text-center py-20 text-gray-600">No analytics data yet</div>;
  }

  const { funnel = [], sourceRoi = [], kpi = {} } = analytics;

  const maxFunnelCount = Math.max(...funnel.map(f => f.count), 1);
  const convRate = kpi.conversion_rate_90d != null ? Math.round(kpi.conversion_rate_90d * 100) : null;

  const stageMap = {};
  for (const s of STAGES) stageMap[s.key] = s;

  return (
    <div className="space-y-6">
      {/* KPI Strip */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total in Pipeline',     value: kpi.total_pipeline ?? 0 },
          { label: 'Conversion Rate (90d)', value: convRate != null ? `${convRate}%` : '—' },
          { label: 'Avg Days to Active',    value: kpi.avg_days_to_active != null ? `${kpi.avg_days_to_active}d` : '—' },
          { label: 'Top Source This Month', value: SOURCE_CONFIG[kpi.topSourceThisMonth]?.label || '—' },
        ].map(({ label, value }) => (
          <div key={label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="text-xs text-gray-500 mb-1">{label}</div>
            <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
          </div>
        ))}
      </div>

      {/* Funnel Chart */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
          <BarChart3 size={16} className="text-amber-400" />
          Pipeline Funnel
        </h3>
        <div className="space-y-2">
          {STAGES.map(s => {
            const data = funnel.find(f => f.stage === s.key);
            const count = data?.count || 0;
            const avg   = data?.avg_days_in_stage || 0;
            const pct   = Math.round((count / maxFunnelCount) * 100);
            return (
              <div key={s.key} className="flex items-center gap-3">
                <div className="w-40 text-xs text-gray-400 text-right flex-shrink-0">{s.label}</div>
                <div className="flex-1 h-7 bg-gray-800 rounded-full overflow-hidden relative">
                  <div
                    className={`h-full ${s.bg} opacity-80 rounded-full transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                  <div className="absolute inset-0 flex items-center px-3 gap-2">
                    <span className="text-xs font-bold text-white tabular-nums">{count}</span>
                    {avg > 0 && <span className="text-xs text-gray-400 tabular-nums">· avg {avg}d</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Source ROI */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
          <TrendingUp size={16} className="text-amber-400" />
          Source ROI
        </h3>
        {sourceRoi.length === 0 ? (
          <div className="text-sm text-gray-600">No data yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  {['Source','Count','Active %','Avg Days to Active'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {sourceRoi.map(row => {
                  const pct = row.total > 0 ? Math.round((row.active_count / row.total) * 100) : 0;
                  return (
                    <tr key={row.source}>
                      <td className="px-3 py-2.5"><SourceBadge source={row.source} /></td>
                      <td className="px-3 py-2.5 text-sm text-white tabular-nums">{row.total}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-sm tabular-nums font-medium ${
                          pct >= 50 ? 'text-green-400' : pct >= 25 ? 'text-amber-400' : 'text-red-400'
                        }`}>{pct}%</span>
                      </td>
                      <td className="px-3 py-2.5 text-sm text-gray-400 tabular-nums">
                        {row.avg_days_to_active != null ? `${row.avg_days_to_active}d` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function RecruitingPipeline() {
  const [view, setView]           = useState('board');   // 'board' | 'roster' | 'analytics'
  const [recruits, setRecruits]   = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [error, setError]         = useState(null);

  // Filters
  const [filterManager, setFilterManager] = useState('');
  const [filterSource, setFilterSource]   = useState('');
  const [filterNpn, setFilterNpn]         = useState('');
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [showFilters, setShowFilters]     = useState(false);

  // Modals / slide-out
  const [showAddModal, setShowAddModal]     = useState(false);
  const [selectedRecruit, setSelectedRecruit] = useState(null);
  const [managers, setManagers]             = useState([]);

  // Drag state
  const dragRecruit = useRef(null);

  // ---------------------------------------------------------------------------
  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (filterManager) params.set('manager', filterManager);
    if (filterSource)  params.set('source',  filterSource);
    if (filterNpn)     params.set('npn_status', filterNpn);
    if (filterOverdue) params.set('overdue', 'true');
    return params.toString();
  }, [filterManager, filterSource, filterNpn, filterOverdue]);

  const loadRecruits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs  = buildQuery();
      const res = await fetch(`${BASE}/api/recruits${qs ? `?${qs}` : ''}`, { credentials: 'include' });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Failed to load');
      setRecruits(json.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res  = await fetch(`${BASE}/api/recruits/analytics/funnel`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setAnalytics(json.data);
    } catch (err) {
      console.error('[recruiting] analytics error:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const loadManagers = useCallback(async () => {
    try {
      const res  = await fetch(`${BASE}/api/recruits/managers/list`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setManagers(json.data || []);
    } catch {}
  }, []);

  useEffect(() => { loadRecruits(); loadManagers(); }, [loadRecruits, loadManagers]);
  useEffect(() => { if (view === 'analytics') loadAnalytics(); }, [view, loadAnalytics]);

  // ---------------------------------------------------------------------------
  // Drag-and-drop handlers
  const handleDragStart = (e, recruit) => {
    dragRecruit.current = recruit;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    const r = dragRecruit.current;
    if (!r || r.stage === targetStage) return;

    // Optimistic update
    setRecruits(prev => prev.map(x =>
      x.id === r.id ? { ...x, stage: targetStage, stageEnteredAt: new Date().toISOString(), daysInStage: 0, isOverdue: false } : x
    ));

    try {
      const res = await fetch(`${BASE}/api/recruits/${r.id}/stage`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: targetStage }),
      });
      const json = await res.json();
      if (json.ok) {
        setRecruits(prev => prev.map(x => x.id === r.id ? json.data : x));
        if (selectedRecruit?.id === r.id) setSelectedRecruit(json.data);
      } else {
        // Rollback
        setRecruits(prev => prev.map(x => x.id === r.id ? r : x));
      }
    } catch {
      setRecruits(prev => prev.map(x => x.id === r.id ? r : x));
    }

    dragRecruit.current = null;
  };

  // ---------------------------------------------------------------------------
  const handleRecruitAdded = (newRecruit) => {
    setRecruits(prev => [newRecruit, ...prev]);
    setShowAddModal(false);
  };

  const handleRecruitUpdated = (updated) => {
    setRecruits(prev => prev.map(r => r.id === updated.id ? updated : r));
    if (selectedRecruit?.id === updated.id) setSelectedRecruit(updated);
  };

  const handleArchive = async (recruit) => {
    if (!window.confirm(`Archive ${recruit.fullName}?`)) return;
    try {
      const res = await fetch(`${BASE}/api/recruits/${recruit.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'manual archive' }),
      });
      const json = await res.json();
      if (json.ok) {
        setRecruits(prev => prev.filter(r => r.id !== recruit.id));
        if (selectedRecruit?.id === recruit.id) setSelectedRecruit(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ---------------------------------------------------------------------------
  // Compute boards per stage
  const boardByStage = {};
  for (const s of STAGES) {
    boardByStage[s.key] = recruits.filter(r => r.stage === s.key);
  }

  const activeFilterCount = [filterManager, filterSource, filterNpn, filterOverdue].filter(Boolean).length;

  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Page header */}
      <div className="border-b border-gray-800 bg-gray-900 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Users size={22} className="text-amber-400" />
              Recruiting Pipeline
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {recruits.length} recruit{recruits.length !== 1 ? 's' : ''} in pipeline
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadRecruits}
              className="p-2 text-gray-500 hover:text-gray-300 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-400 text-gray-950 rounded-lg text-sm font-semibold hover:bg-amber-300 transition-colors"
            >
              <UserPlus size={16} />
              Add Recruit
            </button>
          </div>
        </div>

        {/* View tabs */}
        <div className="flex items-center gap-1 mt-4">
          {[
            { key: 'board',     label: 'Pipeline Board', icon: LayoutGrid },
            { key: 'roster',    label: 'Agent Roster',   icon: Table2 },
            { key: 'analytics', label: 'Analytics',      icon: BarChart3 },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                view === key
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}

          {/* Filters toggle */}
          {view !== 'analytics' && (
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setShowFilters(f => !f)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'bg-amber-500/20 border border-amber-500/30 text-amber-400'
                    : 'bg-gray-800 text-gray-400 hover:text-gray-300'
                }`}
              >
                <Filter size={14} />
                Filters
                {activeFilterCount > 0 && (
                  <span className="bg-amber-400 text-gray-950 text-xs font-bold px-1.5 py-0.5 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Filter bar */}
        {showFilters && view !== 'analytics' && (
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <select
              value={filterSource}
              onChange={e => setFilterSource(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500"
            >
              <option value="">All Sources</option>
              {Object.entries(SOURCE_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>

            <select
              value={filterNpn}
              onChange={e => setFilterNpn(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500"
            >
              <option value="">All NPN Status</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
              <option value="issues">Issues</option>
            </select>

            <select
              value={filterManager}
              onChange={e => setFilterManager(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500"
            >
              <option value="">All Managers</option>
              {managers.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filterOverdue}
                onChange={e => setFilterOverdue(e.target.checked)}
                className="accent-amber-400"
              />
              <span className="text-sm text-gray-400">Overdue only</span>
            </label>

            {activeFilterCount > 0 && (
              <button
                onClick={() => { setFilterManager(''); setFilterSource(''); setFilterNpn(''); setFilterOverdue(false); }}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300"
              >
                <X size={12} /> Clear filters
              </button>
            )}

            <button
              onClick={loadRecruits}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-400 text-gray-950 rounded-lg text-sm font-semibold hover:bg-amber-300 transition-colors ml-auto"
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="p-6">
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 mb-4 flex items-center gap-3 text-red-300">
            <AlertTriangle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {loading && view !== 'analytics' ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={28} className="animate-spin text-amber-400" />
          </div>
        ) : (
          <>
            {/* Board view */}
            {view === 'board' && (
              <div className="overflow-x-auto pb-4">
                <div className="flex gap-3 min-w-max">
                  {STAGES.map(stage => (
                    <KanbanColumn
                      key={stage.key}
                      stage={stage}
                      recruits={boardByStage[stage.key] || []}
                      onCardClick={r => setSelectedRecruit(r)}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Roster view */}
            {view === 'roster' && (
              <AgentRoster
                recruits={recruits}
                onRowClick={r => setSelectedRecruit(r)}
                onArchive={handleArchive}
              />
            )}

            {/* Analytics view */}
            {view === 'analytics' && (
              <AnalyticsView analytics={analytics} loading={analyticsLoading} />
            )}
          </>
        )}
      </div>

      {/* Add recruit modal */}
      {showAddModal && (
        <AddRecruitModal
          onClose={() => setShowAddModal(false)}
          onSave={handleRecruitAdded}
          managers={managers}
        />
      )}

      {/* Detail slide-out */}
      {selectedRecruit && (
        <DetailSlideout
          recruit={selectedRecruit}
          onClose={() => setSelectedRecruit(null)}
          onUpdate={handleRecruitUpdated}
        />
      )}
    </div>
  );
}
